import type { CaptureAsset } from "./evidence-viewer";
export type ReviewState =
  | "pending"
  | "kept"
  | "delete_requested"
  | "deleted"
  | "held";
export type PublishStage = "review" | "run" | "posted" | "verify" | "done";
export type PublishFailure =
  | "review_rejected"
  | "needs_human"
  | "unknown_result"
  | "verify_failed";
export type Publication = {
  id: string;
  runId: string;
  execution: string;
  target: string;
  title: string;
  machine: string;
  account: string;
  stage: PublishStage;
  failure?: PublishFailure;
  verdict: "일치" | "불일치" | "확인 불가";
  reason: string;
  postedAt: string;
  url?: string;
  captures: string[];
  captureAssets?: CaptureAsset[];
  review: ReviewState;
  memo?: string;
  history?: string[];
};
export const reviewLabels: Record<ReviewState, string> = {
  pending: "검토 대기",
  kept: "유지",
  delete_requested: "삭제 요청",
  deleted: "삭제됨",
  held: "보류",
};
export const failureLabels: Record<PublishFailure, string> = {
  review_rejected: "검토 반려",
  needs_human: "사람 확인 필요",
  unknown_result: "결과 불명",
  verify_failed: "검토 불일치",
};
export const stageLabels = [
  "원고 검토",
  "작업 수행",
  "작업 완료",
  "완료 검토",
  "최종 보고",
];
export const stageKeys: PublishStage[] = [
  "review",
  "run",
  "posted",
  "verify",
  "done",
];
export const publicationFixtures: Publication[] = [
  {
    id: "RES-2041",
    runId: "RUN-1054",
    execution: "moru",
    target: "onyou",
    title: "가을 건강검진, 미리 확인할 항목",
    machine: "매장 PC",
    account: "moru_daily",
    stage: "done",
    verdict: "일치",
    reason: "상호·지역, 제목과 본문 첫 문단, 첨부 3장이 원고와 일치합니다.",
    postedAt: "2026.10.15 14:08",
    url: "https://blog.naver.com/example/223000000041",
    captures: ["내 블로그 글 목록", "게시글 제목과 첫 문단", "첨부 사진 확인"],
    review: "pending",
  },
  {
    id: "RES-2040",
    runId: "RUN-1053",
    execution: "onyou",
    target: "onyou",
    title: "환절기 건강 관리 안내",
    machine: "서울 접수 PC",
    account: "onyou_clinic",
    stage: "verify",
    failure: "verify_failed",
    verdict: "불일치",
    reason:
      "제목과 첫 문단은 일치하지만 첨부 사진이 원고 3장 중 2장만 확인됩니다. 게시글은 남아 있습니다.",
    postedAt: "2026.10.15 13:52",
    url: "https://blog.naver.com/example/223000000040",
    captures: ["내 블로그 글 목록", "첨부 사진 불일치"],
    review: "pending",
  },
  {
    id: "RES-2039",
    runId: "RUN-1052",
    execution: "moru",
    target: "onyou",
    title: "건강검진 예약 전 확인 사항",
    machine: "매장 PC",
    account: "moru_daily",
    stage: "posted",
    failure: "unknown_result",
    verdict: "확인 불가",
    reason:
      "게시 버튼을 누른 직후 연결이 끊겼습니다. 재연결 후 제목·시각 대조가 필요합니다.",
    postedAt: "2026.10.15 13:40 · 미확인",
    captures: [],
    review: "pending",
  },
  {
    id: "RES-2038",
    runId: "RUN-1051",
    execution: "onyou",
    target: "onyou",
    title: "주말 진료 안내",
    machine: "서울 접수 PC",
    account: "onyou_clinic",
    stage: "verify",
    failure: "needs_human",
    verdict: "확인 불가",
    reason:
      "게시 후 계정 인증 화면이 나타나 결과를 대조할 수 없습니다. 현장 로그인 후 확인해 주세요.",
    postedAt: "2026.10.15 13:21 · 미확인",
    captures: [],
    review: "held",
    memo: "현장 담당자에게 로그인 확인 요청 · 김현진 13:30",
  },
  {
    id: "RES-2037",
    runId: "RUN-1050",
    execution: "moru",
    target: "moru",
    title: "가을 제철 메뉴 소개",
    machine: "매장 PC",
    account: "moru_daily",
    stage: "done",
    verdict: "일치",
    reason: "제목·본문·첨부가 모두 일치합니다.",
    postedAt: "2026.10.15 12:10",
    url: "https://blog.naver.com/example/223000000037",
    captures: ["게시글 제목과 첫 문단"],
    review: "kept",
    memo: "원고 대조 완료 · 김현진 12:20",
  },
  {
    id: "RES-2036",
    runId: "RUN-1049",
    execution: "moru",
    target: "onyou",
    title: "진료 일정 정정 안내",
    machine: "매장 PC",
    account: "moru_daily",
    stage: "verify",
    failure: "verify_failed",
    verdict: "불일치",
    reason: "게시된 진료 시간이 원고와 다릅니다.",
    postedAt: "2026.10.15 11:55",
    url: "https://blog.naver.com/example/223000000036",
    captures: ["게시글 제목과 첫 문단"],
    review: "delete_requested",
    memo: "잘못된 진료 시간 안내로 삭제 요청 · 김현진 12:00",
  },
  {
    id: "RES-2035",
    runId: "RUN-1043",
    execution: "onyou",
    target: "onyou",
    title: "이전 진료 시간 안내",
    machine: "서울 접수 PC",
    account: "onyou_clinic",
    stage: "done",
    verdict: "일치",
    reason: "게시 완료 후 관리자이 오래된 안내의 삭제를 요청했습니다.",
    postedAt: "2026.10.14 09:00",
    url: "https://blog.naver.com/example/223000000035",
    captures: ["게시글 제목과 첫 문단"],
    review: "deleted",
    memo: "삭제 완료 캡처 확인 · 김현진 10.14 09:30",
  },
];
export const rejectedPublication: Publication = {
  ...publicationFixtures[0],
  id: "RES-REJECTED",
  runId: "RUN-1055",
  stage: "review",
  failure: "review_rejected",
  verdict: "불일치",
  reason:
    "대상은 온유의원인데 원고에 실행 고객사 모루식당의 상호와 메뉴가 포함되어 있습니다. 디바이스 조작 전에 중단했습니다.",
  url: undefined,
  postedAt: "게시 전",
  captures: [],
};
